import { BaseSyntheticEvent, useCallback, useMemo, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useQueryClient } from "react-query";
import Enumerable from "linq";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { FilterMatchMode } from "primereact/api";
import { Ride } from "../../queries/models/ride.model";
import { useToast } from "../../components/ui/toast-context-provider";
import { useRidesQuery } from "../../queries/rides.query";
import { useRidesReportQuery } from "../../queries/reporting.query";
import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  endOfMonth,
  getDate,
  getUnixTime,
  lightFormat,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { useSizeTypesQuery } from "../../queries/size-types.query";
import { useActiveHorseGroupsAsSelectOptionQuery } from "../../queries/horse-groups.query";

const Period = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
};

export function Reports() {
  const queryClient = useQueryClient();
  const [fromDate, setFromDate] = useState(startOfMonth(new Date()));
  const [toDate, setToDate] = useState(endOfMonth(new Date()));
  const ridesReportQuery = useRidesReportQuery(fromDate, toDate);
  const sizeTypeQuary = useSizeTypesQuery();

  const [selectedRiders, setSelectedRiders] = useState<string[]>([]);
  const [selectedHorses, setSelectedHorses] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedRideTypes, setSelectedRideTypes] = useState<string[]>([]);
  const [selectedHorseSizes, setSelectedHorseSizes] = useState<string[]>([]);
  const [selectedRiderSizes, setSelectedRiderSizes] = useState<string[]>([]);
  const [period, setPeriod] = useState(Period.MONTH);

  const periodOptions = [
    { label: "Dzień", value: Period.DAY },
    { label: "Tydzień", value: Period.WEEK },
    { label: "Miesiąc", value: Period.MONTH },
  ];

  const sizeTypesOptions = useMemo(() => {
    return (
      sizeTypeQuary.data?.map((st) => ({ label: st.name, value: st.name })) ??
      []
    );
  }, [sizeTypeQuary.data]);

  const allRiders = useMemo(() => {
    return (
      Enumerable.from(ridesReportQuery.data ?? [])
        .distinct((r) => r.rider)
        .select((r) => ({ label: r.rider, value: r.rider }))
        .orderBy((r) => r.label)
        .toArray() ?? []
    );
  }, [ridesReportQuery.data]);

  const allHorses = useMemo(() => {
    return (
      Enumerable.from(ridesReportQuery.data ?? [])
        .distinct((r) => r.horse)
        .select((r) => ({ label: r.horse, value: r.horse }))
        .orderBy((r) => r.label)
        .toArray() ?? []
    );
  }, [ridesReportQuery.data]);

  const allUsers = useMemo(() => {
    return (
      Enumerable.from(ridesReportQuery.data ?? [])
        .distinct((r) => r.user)
        .select((r) => ({ label: r.user, value: r.user }))
        .orderBy((r) => r.label)
        .toArray() ?? []
    );
  }, [ridesReportQuery.data]);

  const allRideTypes = useMemo(() => {
    return (
      Enumerable.from(ridesReportQuery.data ?? [])
        .distinct((r) => r.rideType)
        .select((r) => ({ label: r.rideType, value: r.rideType }))
        .orderBy((r) => r.label)
        .toArray() ?? []
    );
  }, [ridesReportQuery.data]);

  const [selectedHorseGroups, setSelectedHorseGroups] = useState<number[]>([]);
  const horseGroupOptionsQuery = useActiveHorseGroupsAsSelectOptionQuery();

  const filteredData = useMemo(() => {
    return (
      Enumerable.from(ridesReportQuery.data ?? [])
        .where((r) =>
          selectedRiders.length > 0 ? selectedRiders.includes(r.rider) : true
        )
        .where((r) =>
          selectedHorses.length > 0 ? selectedHorses.includes(r.horse) : true
        )
        .where((r) =>
          selectedHorseGroups.length > 0
            ? (r as any).horseGroupIds?.some((id: number) => selectedHorseGroups.includes(id))
            : true
        )
        .where((r) =>
          selectedUsers.length > 0 ? selectedUsers.includes(r.user) : true
        )
        .where((r) =>
          selectedHorseSizes.length > 0
            ? selectedHorseSizes.includes(r.horseSize)
            : true
        )
        .where((r) =>
          selectedRiderSizes.length > 0
            ? r.riderSizes.some((s) => selectedRiderSizes.includes(s))
            : true
        )
        .where((r) =>
          selectedRideTypes.length > 0
            ? selectedRideTypes.includes(r.rideType)
            : true
        )
        .toArray() ?? []
    );
  }, [
    ridesReportQuery.data,
    selectedRiders,
    selectedHorses,
    selectedUsers,
    selectedHorseSizes,
    selectedRiderSizes,
    selectedRideTypes,
    selectedHorseGroups,
  ]);

  const filteredDataGroupdByDate = useMemo(() => {
    return (
      Enumerable.from(filteredData)
        .groupBy((r) => {
          let periodStart;

          switch (period) {
            case Period.WEEK:
              periodStart = startOfWeek(new Date(r.date), { weekStartsOn: 1 });
              break;
            case Period.MONTH:
              periodStart = startOfMonth(new Date(r.date));
              break;
            case Period.DAY:
            default:
              periodStart = startOfDay(new Date(r.date));
              break;
          }

          return getUnixTime(periodStart) * 1000;
        })
        .orderBy((g) => g.key())
        .select((g) => {
          return {
            date: new Date(g.key()),
            value: g.getSource().length,
            rides: g.getSource(),
            chargeableCount: g.getSource().filter((r) => r.isChargeable).length,
            nonChargeableCount: g.getSource().filter((r) => !r.isChargeable)
              .length,
          };
        })
        .toArray() ?? []
    );
  }, [filteredData, period]);

  const totalChargeableRidesCount = useMemo(() => {
    return Enumerable.from(filteredData).count((r) => r.isChargeable);
  }, [filteredData]);

  const filteredDataGroupdByHorse = useMemo(() => {
    return (
      Enumerable.from(filteredData)
        .groupBy((r) => r.horse)
        .orderBy((g) => g.key())
        .select((g) => {
          return {
            label: g.key(),
            value: g.getSource().length,
            chargeableCount: g.getSource().filter((r) => r.isChargeable).length,
            nonChargeableCount: g.getSource().filter((r) => !r.isChargeable)
              .length,
            rides: g.getSource(),
          };
        })
        .toArray() ?? []
    );
  }, [filteredData]);

  // Godziny przepracowane przez instruktora (liczone po unikalnych przedziałach godzinnych)
  const userHoursChartOptions = useMemo(() => {
    const perUserHours = Enumerable.from(filteredData)
      .groupBy((r) => r.user)
      .orderBy((g) => g.key())
      .select((g) => {
        const hoursCount = Enumerable.from(g.getSource())
          .select((r) => {
            const d = new Date(r.date);
            d.setMinutes(0, 0, 0); // bucket do pełnej godziny
            return d.getTime();
          })
          .distinct()
          .count();

        return { user: g.key(), hours: hoursCount };
      })
      .toArray();

    const categories = perUserHours.map((x) => x.user);
    const values = perUserHours.map((x) => x.hours);

    return {
      grid: { top: 8, right: 16, bottom: 60, left: 40 },
      xAxis: {
        type: "category",
        name: "Instruktor",
        data: categories,
        axisLabel: { rotate: 45 },
      },
      yAxis: { type: "value", name: "Godziny" },
      series: [
        {
          name: "Godziny przepracowane",
          type: "bar",
          data: values,
          itemStyle: { color: "#00796B" },
          label: { show: true, position: "top" },
        },
      ],
      tooltip: { show: true, trigger: "item" },
    } as EChartsOption;
  }, [filteredData]);

  const avgDayCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    return ((filteredData?.length ?? 0) / daysDiff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  const avgWeekCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    const diff = daysDiff / 7;
    return ((filteredData?.length ?? 0) / diff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  const avgMonthCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    const diff = daysDiff / 30;
    return ((filteredData?.length ?? 0) / diff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  // Średnie tylko dla płatnych jazd
  const avgDayChargeableCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    const chargeable = Enumerable.from(filteredData).count((r) => r.isChargeable);
    return (chargeable / daysDiff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  const avgWeekChargeableCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    const diff = daysDiff / 7;
    const chargeable = Enumerable.from(filteredData).count((r) => r.isChargeable);
    return (chargeable / diff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  const avgMonthChargeableCount = useMemo(() => {
    const daysDiff = differenceInDays(toDate, fromDate) + 1;
    const diff = daysDiff / 30;
    const chargeable = Enumerable.from(filteredData).count((r) => r.isChargeable);
    return (chargeable / diff).toFixed(1);
  }, [fromDate, toDate, filteredData]);

  const chartOptions = useMemo(() => {
    const ridesDateDataChargeable = filteredDataGroupdByDate.map(
      (z) =>
        [getUnixTime(new Date(z.date)) * 1000, z.chargeableCount] satisfies [
          number,
          number
        ]
    );

    const ridesDateDataNonChargeable = filteredDataGroupdByDate.map(
      (z) =>
        [getUnixTime(new Date(z.date)) * 1000, z.nonChargeableCount] satisfies [
          number,
          number
        ]
    );

    const chargeableSerie = {
      data: ridesDateDataChargeable.map(([time, value]) => [time, value]),
      name: "Płatne jazdy",
      type: "bar",
      stack: "total",
      barGap: "30%", // spacing between bars
      itemStyle: {
        color: "#303F9F",
      },
      label: {
        show: true,
        position: "insideLeft",
      },
      emphasis: {
        focus: "series",
      },
    };

    const nonChargeableSerie = {
      data: ridesDateDataNonChargeable.map(([time, value]) => [time, value]),
      name: "Niepłatne jazdy",
      type: "bar",
      stack: "total",
      barGap: "30%", // spacing between bars
      itemStyle: {
        color: "#D32F2F",
      },
      label: {
        show: true,
        position: "insideRight",
      },
      emphasis: {
        focus: "series",
      },
    };

    return {
      grid: { top: 18, right: 16, bottom: 64, left: 32 },
      xAxis: {
        type: "time",
        name: "Data",
        axisLabel: {
          interval: "auto", // Automatically determine interval
          hideOverlap: true, // Hide overlapping labels
        },
      },
      yAxis: {
        type: "value",
        name: "Liczba jazd",
      },
      dataZoom: [
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "none",
        },
      ],
      series: [chargeableSerie, nonChargeableSerie],
      tooltip: {
        show: true,
        trigger: "axis",
        triggerOn: "click",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          const total = list.reduce((sum, p) => {
            // p.data may be [time, value]
            const val = Array.isArray(p?.data) ? p.data[1] : (p?.value?.[1] ?? p?.data ?? 0);
            return sum + (typeof val === 'number' ? val : 0);
          }, 0);
          const lines = list
            .map((p) => `${p.marker} ${p.seriesName}: <b>${Array.isArray(p.data) ? p.data[1] : (p?.value?.[1] ?? p.data ?? 0)}</b>`)
            .join('<br/>');
          const dateLabel = list?.[0]?.axisValueLabel ?? '';
          return `${dateLabel}<br/>${lines}<br/><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background:#455A64"></span> Wszystkie jazdy: <b>${total}</b>`;
        },
      },
    } as EChartsOption;
  }, [filteredDataGroupdByDate]);

  const horseChartOptions = useMemo(() => {
    const ridesDateDataChargeable = filteredDataGroupdByHorse.map(
      (z) => [z.label, z.chargeableCount] satisfies [string, number]
    );

    const ridesDateDataNonChargeable = filteredDataGroupdByHorse.map(
      (z) => [z.label, z.nonChargeableCount] satisfies [string, number]
    );

    const chargeableSerie = {
      data: ridesDateDataChargeable.map(([_, value]) => value),
      name: "Płatne jazdy",
      type: "bar",
      stack: "total",
      itemStyle: {
        color: "#303F9F",
      },
      label: {
        show: true,
        position: "insideLeft",
      },
      emphasis: {
        focus: "series",
      },
    };

    const nonChargeableSerie = {
      data: ridesDateDataNonChargeable.map(([_, value]) => value),
      name: "Niepłatne jazdy",
      type: "bar",
      stack: "total",
      itemStyle: {
        color: "#D32F2F",
      },
      label: {
        show: true,
        position: "insideRight",
      },
      emphasis: {
        focus: "series",
      },
    };

    return {
      grid: { top: 8, right: 16, bottom: 60, left: 32 },
      xAxis: {
        type: "category",
        name: "Koń",
        data: filteredDataGroupdByHorse.map((z) => z.label), // Use horse names as categories
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: {
        type: "value",
        name: "Liczba jazd",
      },
      series: [chargeableSerie, nonChargeableSerie],
      tooltip: {
        show: true,
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          const extractVal = (p: any) => {
            if (typeof p?.data === 'number') return p.data;
            if (Array.isArray(p?.data)) return p.data[1];
            if (Array.isArray(p?.value)) return p.value[1];
            return Number(p?.value ?? 0);
          };
          const total = list.reduce((sum, p) => sum + (Number.isFinite(extractVal(p)) ? extractVal(p) : 0), 0);
          const lines = list.map((p) => `${p.marker} ${p.seriesName}: <b>${extractVal(p)}</b>`).join('<br/>');
          const catLabel = list?.[0]?.axisValueLabel ?? '';
          return `${catLabel}<br/>${lines}<br/><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background:#455A64"></span> Wszystkie jazdy: <b>${total}</b>`;
        },
      },
      legend: {
        data: ["Chargeable Rides", "Non-Chargeable Rides"],
      },
    } as EChartsOption;
  }, [filteredDataGroupdByHorse]);

  const handleDownloadExcel = useCallback(async () => {
    import("xlsx").then((xlsx) => {
      const ws_header = [
        "Data",
        "Rodzaj jazdy",
        "Jeździec",
        "Wzrost jeźdźca",
        "Koń",
        "Rozmiar konia",
        "Instruktor",
        "Czy płatne",
      ];
      const ws_rows = ridesReportQuery!
        .data!.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )
        .map((x) => [
          new Date(x.date).toLocaleString("pl-PL"),
          x.rideType,
          x.rider,
          x.riderSizes.join(", "),
          x.horse,
          x.horseSize,
          x.user,
          x.isChargeable ? "Tak" : "Nie",
        ]);

      const ws_data = [ws_header, ...ws_rows];

      const worksheet = xlsx.utils.aoa_to_sheet(ws_data);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "raport_jazd");
    });
  }, [ridesReportQuery]);

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          data,
          fileName +
            "_" +
            lightFormat(new Date(), "yyyy-MM-dd_HH-mm") +
            EXCEL_EXTENSION
        );
      }
    });
  };

  return (
    <div className="w-full">
      <div className="flex flex-row m-2 gap-2">
        <Calendar
          className="w-6"
          locale="pl"
          dateFormat="dd MM yy"
          onChange={(x) => setFromDate(x.value as Date)}
          value={fromDate}
        />

        <Calendar
          className="w-6"
          locale="pl"
          dateFormat="dd MM yy"
          onChange={(x) => setToDate(x.value as Date)}
          value={toDate}
        />
      </div>
      <div className="m-2">
        <Dropdown
          value={period}
          onChange={(e) => setPeriod(e.value)}
          options={periodOptions}
          optionLabel="label"
          placeholder="Grupuj po"
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedHorseGroups}
          onChange={(e) => setSelectedHorseGroups(e.value)}
          options={horseGroupOptionsQuery.data ?? []}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj grupy koni"
          maxSelectedLabels={2}
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedHorses}
          onChange={(e) => setSelectedHorses(e.value)}
          options={allHorses}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj konie"
          maxSelectedLabels={3}
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedRiders}
          onChange={(e) => setSelectedRiders(e.value)}
          options={allRiders}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj jeźdźców"
          maxSelectedLabels={3}
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedHorseSizes}
          onChange={(e) => setSelectedHorseSizes(e.value)}
          options={sizeTypesOptions}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj rozmiar konia"
          maxSelectedLabels={3}
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedUsers}
          onChange={(e) => setSelectedUsers(e.value)}
          options={allUsers}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj instruktorów"
          maxSelectedLabels={3}
          className="w-full md:w-20rem"
        />
      </div>
      <div className="m-2">
        <MultiSelect
          value={selectedRiderSizes}
          onChange={(e) => setSelectedRiderSizes(e.value)}
          options={sizeTypesOptions}
          optionLabel="label"
          display="chip"
          placeholder="Filtruj wzrost jeźdźców"
          maxSelectedLabels={3}
          className="w-full md:w-20rem"
        />
      </div>

      <div className="px-2 pt-3">
        <h3 className="text-lg font-semibold">
          Jazdy per koń (płatne/niepłatne)
        </h3>
        <p className="text-sm opacity-70">
          Słupki pokazują liczbę jazd na poszczególnych koniach w wybranym
          okresie, rozbite na płatne i niepłatne.
        </p>
      </div>
      <div>
        <ReactECharts
          className={"w-full h-12rem p-0 m-0"}
          option={horseChartOptions}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>

      <div className="px-2 pt-4">
        <h3 className="text-lg font-semibold">Jazdy w czasie</h3>
        <p className="text-sm opacity-70">
          Liczba jazd w czasie, grupowana wg wybranego okresu
          (dzień/tydzień/miesiąc) oraz rozbita na płatne i niepłatne.
        </p>
      </div>
      <div>
        <ReactECharts
          className={"w-full h-12rem p-0 m-0"}
          option={chartOptions}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>

      {/* Nowy wykres: Godziny przepracowane przez instruktorów */}
      <div className="px-2 pt-4">
        <h3 className="text-lg font-semibold">
          Godziny przepracowane przez instruktorów
        </h3>
        <p className="text-sm opacity-70">
          Suma unikalnych godzin pracy (kubełkowanych do pełnej godziny)
          przypadających na każdego instruktora w wybranym zakresie.
        </p>
      </div>
      <div>
        <ReactECharts
          className={"w-full h-12rem p-0 m-0"}
          option={userHoursChartOptions}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>

      <div>
        <table className="table-auto text-xl p-2">
          <tbody>
            <tr>
              <td>Średnia dzień: </td>
              <td className="font-bold pl-4">
                {avgDayCount} <span className="opacity-70">(płatne: {avgDayChargeableCount})</span>
              </td>
            </tr>
            <tr>
              <td>Średnia tydzień: </td>
              <td className="font-bold pl-4">
                {avgWeekCount} <span className="opacity-70">(płatne: {avgWeekChargeableCount})</span>
              </td>
            </tr>
            <tr>
              <td>Średnia miesiąc: </td>
              <td className="font-bold pl-4">
                {avgMonthCount} <span className="opacity-70">(płatne: {avgMonthChargeableCount})</span>
              </td>
            </tr>
            <tr>
              <td>Total</td>
              <td className="font-bold pl-4">{filteredData.length}</td>
            </tr>
            <tr>
              <td>Total płatnych</td>
              <td className="font-bold pl-4">{totalChargeableRidesCount}</td>
            </tr>
            <tr>
              <td>Pobierz plik</td>
              <td className="font-bold pl-4">
                <button
                  className="p-button p-component p-button-outlined"
                  onClick={handleDownloadExcel}
                >
                  EXCEL
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
